<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="dropdown-item page-item">
        <div class="page-grid">
          <template v-if="!isDistributorUser">
            <div class="page-search-container dropdown-item">
              <div style="display: flex; align-items: center; gap: 10px;">
                <label for="distributor" style="font-weight:700; white-space: nowrap;">Distributor : </label>
                <select
                    id="distributor"
                    v-model="selectedDistributor"
                    class="scrollable-dropdown select-distributor"
                    @change="fetchDealerGroups"
                >
                  <option value="">All Distributors</option>
                  <option v-for="distributor in distributorList" :key="distributor.profile_id" :value="distributor.profile_id">
                    {{ distributor.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="page-search-container dropdown-item">
              <input
                  type="text"
                  placeholder="Search dealer groups..."
                  class="search-input"
                  v-model="searchFilter"
              />
            </div>

            <button type="button" class="btn button-default" @click="addNewGroup">
              Add New Dealer Group
            </button>
          </template>

          <template v-else>
            <div class="page-search-container dropdown-item">
              <input
                  type="text"
                  placeholder="Search dealer groups..."
                  class="search-input"
                  v-model="searchFilter"
              />
            </div>

            <button type="button" class="btn button-default" @click="addNewGroup">
              Add New Dealer Group
            </button>
          </template>
        </div>
      </div>

      <div v-if="loading" class="loading-spinner mt-3">
        <i class="fa fa-spinner fa-spin"></i> Loading...
      </div>

      <div v-if="!loading && dealerGroups.length > 0" class="table-container">
        <table>
          <thead>
          <tr>
            <th class="col-name">Dealer Group & Description</th>
            <th class="text-center col-count">Dealers Count</th>
            <th class="col-date">Created Datetime</th>
            <th class="text-center col-status">Status</th>
            <th class="col-action">Action</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="group in filteredGroups" :key="group.id">
            <td>
              <div class="group-name" :title="group.name">{{ group.name }}</div>
              <div v-if="group.description" class="group-description" :title="group.description">
                ({{ group.description }})
              </div>
            </td>
            <td class="text-center">{{ group.dealers_count || '-' }}</td>
            <td>{{ formatDateTime(group.created_at) }}</td>
            <td class="text-center">
              <span
                  class="status-badge"
                  :class="{
                    active: group.is_active === '1',
                    inactive: group.is_active === '0',
                  }"
              >
                {{ group.is_active === '1' ? 'Active' : 'Inactive' }}
              </span>
            </td>

            <td>  
              <i class="fa fa-edit" @click="editGroup(group.id)" title="Edit"></i>
              <i class="fa fa-eye action-icon" aria-hidden="true" @click="viewGroup(group.id)" title="View"></i>
            </td>
          </tr>

          <tr v-if="filteredGroups.length === 0">
            <td colspan="5" class="text-center">
              <span v-if="!loading">No dealer groups found matching your criteria.</span>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Show No Data Found Component  -->
      <div v-if="!loading && dealerGroups.length === 0" class="not-found-widget">
        <img src="../../../assets/vectors/data_not_found.png" alt="No Dealer Groups" class="error-image" />
        <p class="text-center error-message">Dealer Groups Not Found!</p>
      </div>
    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { getDealerGroupsList, fetchDistributorsList } from "@/services/dataService";

export default {
  name: 'DealerGroupsList',
  data() {
    return {
      loading: false,
      token: localStorage.getItem("token_set") || "",
      profileId: localStorage.getItem("profile_id"),
      reportingProfileId: localStorage.getItem("reportingL1ProfileId"),
      userCategory: localStorage.getItem("user_label"),
      
      searchFilter: "",
      selectedDistributor: "", 
      isDistributorUser: false,
      
      distributorList: [],
      dealerGroups: [],
    }
  },
  computed: {
    filteredGroups() {
      let filterDistId = "";

      if (this.isDistributorUser) {
        filterDistId = this.userCategory === 'DISTRIBUTOR BACK OFFICE' ? this.reportingProfileId : this.profileId;
      } else {
        filterDistId = this.selectedDistributor;
      }

      return this.dealerGroups.filter(group => {
        const matchesDistributor = filterDistId ? (String(group.distributor_id) === String(filterDistId)) : true;

        const searchTerm = this.searchFilter.toLowerCase();
        const matchesSearch = 
            (group.name || '').toLowerCase().includes(searchTerm) ||
            (group.description || '').toLowerCase().includes(searchTerm);

        return matchesDistributor && matchesSearch;
      });
    }
  },
  methods: {
    // Helper to truncate long strings
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },

    // Fetch Dealer Groups List
    async fetchDealerGroups() {
      try {
        this.loading = true;
        let idToUse = "";
        if (this.isDistributorUser) {
          idToUse = this.userCategory === 'DISTRIBUTOR BACK OFFICE' ? this.reportingProfileId : this.profileId;
        } else {
          idToUse = this.selectedDistributor || this.profileId;
        }

        const response = await getDealerGroupsList(this.token, idToUse);
        
        if (response.data && response.data.status === 'success' && response.data.data) {
          this.dealerGroups = Array.isArray(response.data.data) 
            ? response.data.data 
            : (response.data.data.dealerGroups || []);
        } else {
          this.dealerGroups = [];
        }
      } catch (error) {
        console.error("Error fetching dealer groups list:", error);
        this.dealerGroups = [];
      } finally {
        this.loading = false;
      }
    },

    addNewGroup() {
      this.$router.push('/create-dealer-group');
    },

    editGroup(id) {
      this.$router.push({
        path: '/edit-dealer-group',
        query: { dealerGroupId: id }
      });
    },

    viewGroup(id) {
      this.$router.push({
        path: '/view-dealer-group',
        query: { dealerGroupId: id }
      });
    },

    // Fetch Distributors List
    async fetchDistributors() {
      try {
        const response = await fetchDistributorsList(this.profileId, this.token);
        this.distributorList = response.data.data || [];
      } catch (error) {
        console.error('Error fetching Distributors:', error);
      }
    },

    // Initialize User Settings
    initializeUserSettings() {
      const userLabel = localStorage.getItem("user_label");
      this.isDistributorUser = userLabel === "DISTRIBUTOR" || userLabel === "DISTRIBUTOR BACK OFFICE";

      if (userLabel === "DISTRIBUTOR") {
        this.selectedDistributor = this.profileId;
      } else if (userLabel === "DISTRIBUTOR BACK OFFICE") {
        this.selectedDistributor = this.reportingProfileId;
      }
    },

    // Format DateTime
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '-';
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return dateTimeString; // Return original if invalid
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      
      hours = hours % 12;
      hours = hours ? hours : 12; // the hour '0' should be '12'
      const strHours = String(hours).padStart(2, '0');
      
      return `${year}-${month}-${day} ${strHours}:${minutes} ${ampm}`;
    }
  },
  mounted() {
    this.initializeUserSettings();
    this.fetchDealerGroups();
    if (!this.isDistributorUser) {
      this.fetchDistributors();
    }
  }
};
</script>

<style scoped> 
.page-grid {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 15px;
}

.page-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
}

.page-search-container {
  display: flex;
  align-items: center;
  flex-grow: 1;
}

.page-search-container.dropdown-item {
  flex-grow: 0;
  flex-basis: auto;
}

.search-input {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  width: 100%;
  min-width: 250px;
}

.search-input:hover {
  border-color: #0056b3;
}

.select-distributor {
  padding: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
  min-width: 250px;
}

.select-distributor:hover {
  border-color: #0056b3;
}

.card {
  padding: 10px;
}

.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff;
}

.table-container {
  width: 100%;
  overflow-x: auto;
}

table {
  width: 100%;
  min-width: 800px;
  border-collapse: collapse;
  margin: 10px 0;
  border: none;
  table-layout: fixed;
}

th {
  text-align: left;
  border-bottom: 2px solid #ddd;
  background-color: #f4f4f4;
  padding: 10px;
  white-space: normal;
}

td {
  text-align: left;
  border: none;
  padding: 10px;
  white-space: normal;
  border-bottom: 1px solid #eee;
  word-break: break-word;
  overflow-wrap: break-word;
}

tbody tr:last-child td {
  border-bottom: none;
}

.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}

.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-eye:active {
  transform: scale(1.1);
}

.fa-edit {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
}

.fa-edit:hover {
  color: #0056b3;
  transform: scale(1.2);
}

.fa-edit:active {
  transform: scale(1.1);
}

.status-badge {
  display: inline-block;
  padding: 4px 10px;
  font-size: 13px;
  border-radius: 5px;
  text-align: center;
  width: 100px;       
  max-width: 100px;   
  min-width: 100px; 
  color: #fff;
  box-sizing: border-box;
}

.status-badge.active {
  background-color: #069E27;
}

.status-badge.inactive {
  background-color: #c62828;
}

.not-found-widget {
  display: flex;           
  flex-direction: column;  
  align-items: center;     
  justify-content: center; 
  margin: 50px auto;       
  width: 100%;             
  max-width: 500px;        
}

.not-found-widget img {
  width: 250px;
}

.not-found-widget p {
  color: grey;
  font-size: 18px;
  font-weight: bold;
  margin-top: -25px;
}

input:focus, select:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

@media (max-width: 768px) {
  .page-grid {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .page-search-container {
    width: 100%;
  }

  .search-input, .select-distributor {
    min-width: 100%;
  }
}

.group-name {
  color: #000;
  font-weight: 600;
}

.group-description {
  font-size: 11px;
  color: #777;
  margin-top: 2px;
  line-height: 1.4;
}

.col-name { width: 40%; }
.col-count { width: 15%; }
.col-date { width: 20%; }
.col-status { width: 15%; }
.col-action { width: 10%; }
</style>